import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { ApproveResponse } from '~/hooks/mutations/use-approval-actions';
import { useApprovalExecution, watchDeadline, type ApprovalExecution } from '~/hooks/queries/use-approval-execution';
import { ApiError } from '~/lib/api-client';

/** What the card shows once a run it had to wait on has settled. */
export type LateOutcome =
  | { kind: 'succeeded' | 'failed'; message: string | null }
  | { kind: 'lost' };

const NO_OUTCOMES: ReadonlyMap<string, LateOutcome> = new Map();

/**
 * "HH:MM" by which a deferred card must be answered, in the viewer's own
 * locale/time zone — the card's "approve before 16:00" line. Reads the
 * server's own `answerableUntil` (approvals-service.ts) rather than a copy of
 * its window, so the line cannot drift from what the server enforces. Null
 * when absent or unparseable, so the card leaves the deadline out rather than
 * printing "Invalid Date".
 */
export function lateApprovalDeadline(answerableUntil: string | undefined): string | null {
  if (!answerableUntil) return null;
  const ms = new Date(answerableUntil).getTime();
  if (Number.isNaN(ms)) return null;
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** Only a late run still going when the approve route answered is worth watching. */
export function shouldWatchLateRun(result: ApproveResponse | undefined): boolean {
  return result?.executed === 'running';
}

/**
 * What the desktop approval card shows once a late run it had to wait on
 * (`executed: "running"`) settles. DISPLAY ONLY: this sends no chat turn.
 * Carrying on after a late approval is the server's job (FRONT-3036) - it
 * starts the continuation itself once the step settles, so no tab sends one.
 *
 * Driven from the approve mutation's own response (`onApproved`): only the tab
 * whose click claimed the run holds the answer that says it is running.
 */
export function useLateRunOutcomes(onOutcome?: (approvalId: string, outcome: LateOutcome) => void) {
  const [watching, setWatching] = useState<string[]>([]);
  const [outcomes, setOutcomes] = useState<ReadonlyMap<string, LateOutcome>>(NO_OUTCOMES);

  const onApproved = useCallback((approvalId: string, result: ApproveResponse | undefined) => {
    if (!shouldWatchLateRun(result)) return;
    setWatching((w) => (w.includes(approvalId) ? w : [...w, approvalId]));
  }, []);

  // The watcher's effect re-fires on every render once settled, so the
  // caller's hook is told once per approval.
  const told = useRef<Set<string>>(new Set());
  const onOutcomeRef = useRef(onOutcome);
  onOutcomeRef.current = onOutcome;
  const finishWatching = useCallback((id: string, outcome: LateOutcome) => {
    setOutcomes((prev) => (prev.has(id) ? prev : new Map(prev).set(id, outcome)));
    if (!told.current.has(id)) {
      told.current.add(id);
      onOutcomeRef.current?.(id, outcome);
    }
  }, []);

  const watchers: ReactNode = watching.map((id) => (
    <LateExecutionWatcher key={id} approvalId={id} onSettled={(o) => finishWatching(id, o)} />
  ));

  return {
    onApproved,
    watchers,
    /** The settled outcome of a run the card had to wait on, if any. */
    outcomeOf: (approvalId: string): LateOutcome | undefined => outcomes.get(approvalId),
  };
}

/** Pure for its test: what one poll result means for a watcher. */
export function watcherOutcome(
  data: ApprovalExecution | undefined,
  error: unknown,
  timedOut: boolean,
): LateOutcome | null {
  const s = data?.executionStatus;
  if (s === 'succeeded') return { kind: 'succeeded', message: data?.message ?? null };
  if (s === 'failed' || s === 'refused') return { kind: 'failed', message: data?.message ?? null };
  if (error instanceof ApiError && error.status === 404) return { kind: 'lost' };
  if (timedOut) return { kind: 'lost' };
  return null;
}

/** Polls one running late approval until it settles; renders nothing. */
function LateExecutionWatcher({ approvalId, onSettled }: { approvalId: string; onSettled: (o: LateOutcome) => void }) {
  const [startedAt] = useState(() => Date.now());
  const [timedOut, setTimedOut] = useState(false);
  const { data, error } = useApprovalExecution(approvalId, startedAt);
  const outcome = watcherOutcome(data, error, timedOut);

  // Waits exactly as long as the server's own settle deadline (`settleBy`),
  // re-armed whenever a poll brings a newer answer.
  const deadline = watchDeadline(data, startedAt);
  useEffect(() => {
    const t = setTimeout(() => setTimedOut(true), Math.max(0, deadline - Date.now()));
    return () => clearTimeout(t);
  }, [deadline]);

  useEffect(() => {
    if (outcome) onSettled(outcome);
  }, [outcome, onSettled]);

  return null;
}
